"use strict";
// Shared voice-agent wire contract for mobile and ai-backend.
Object.defineProperty(exports, "__esModule", { value: true });
exports.voiceAgentStreamFrameSchema = exports.voiceAgentResponseSchema = exports.VOICE_CLIENT_ACTIONS = exports.VOICE_USER_INTENTS = exports.voiceAgentRequestSchema = exports.voiceRouteFiltersSchema = exports.voiceActiveRouteSchema = exports.voiceStopSchema = void 0;
const zod_1 = require("zod");
const geo_1 = require("./geo");
const rest_area_1 = require("./rest-area");
// Lean stop projection used by the voice flow.
exports.voiceStopSchema = zod_1.z.object({
    id: zod_1.z.string().max(256),
    name: zod_1.z.string().min(1).max(200),
    coord: geo_1.coordSchema, // [lng, lat]
    availability: rest_area_1.SiteAvailabilityStatusSchema.optional(),
    // Forecast at ETA; absent when no ETA exists.
    predictedAvailabilityAtEta: rest_area_1.SiteAvailabilityStatusSchema.optional(),
    availableSpaces: zod_1.z.number().int().nonnegative().optional(),
    // ISO timestamp for stops on the active route.
    estimatedTimeOfArrival: zod_1.z.string().datetime({ offset: true }).optional(),
    distanceKm: zod_1.z.number().nonnegative().optional(),
});
// Client-owned route snapshot echoed each turn for stop resolution.
exports.voiceActiveRouteSchema = zod_1.z.object({
    destination: zod_1.z.object({ name: zod_1.z.string().min(1).max(200), coord: geo_1.coordSchema }),
    selectedStopId: zod_1.z.string().max(256).optional(),
    stops: zod_1.z.array(exports.voiceStopSchema).max(100),
    committedStops: zod_1.z.array(exports.voiceStopSchema).max(100).optional(),
    avoidTolls: zod_1.z.boolean(),
});
// Voice-supported subset of the mobile route filters.
exports.voiceRouteFiltersSchema = zod_1.z.object({
    maxPrice: zod_1.z.number().nonnegative().max(100_000).optional(),
    hasBooking: zod_1.z.boolean().optional(),
    selectedAmenities: zod_1.z.array(zod_1.z.string().max(64)).max(30).optional(),
});
// Mobile POST body for /voice/agent.
exports.voiceAgentRequestSchema = zod_1.z.object({
    audioBase64: zod_1.z.string().min(1).max(2_000_000),
    threadId: zod_1.z.string().min(1).max(128),
    currentLocation: geo_1.coordSchema, // [lng, lat]
    remainingTimePicker: zod_1.z
        .number()
        .positive()
        .max(16 * 3600)
        .nullable(),
    activeRoute: exports.voiceActiveRouteSchema.nullable().optional(),
    skipFullStops: zod_1.z.boolean().optional().default(true),
    filters: exports.voiceRouteFiltersSchema.optional(),
});
// Driver intents in; mobile dispatch actions out.
exports.VOICE_USER_INTENTS = [
    'PLAN_ROUTE',
    'NEXT_STOPS',
    'GET_DETAILS',
    'CHOOSE_STOP',
    'ADD_STOP',
    'SKIP_STOP',
    'FILTER_STOPS',
    'GET_ETA',
    'START_NAVIGATION',
    'STOP_NAVIGATION',
    'CLARIFICATION_NEEDED',
    'DISMISS',
    'UNKNOWN_ACTION',
];
exports.VOICE_CLIENT_ACTIONS = [
    'ROUTE_UPDATED',
    'STOPS_LIST',
    'STOP_DETAILS',
    'STOP_CHOSEN',
    'ETA_INFO',
    'START_NAVIGATION',
    'STOP_NAVIGATION',
    'CLARIFICATION_NEEDED',
    'DISMISSED',
    'INFO',
    'UNKNOWN_ACTION',
];
// Backend response fields mobile renders; map geometry is added app-side.
exports.voiceAgentResponseSchema = zod_1.z.object({
    action: zod_1.z.enum(exports.VOICE_CLIENT_ACTIONS),
    responseText: zod_1.z.string(),
    recommendedIndex: zod_1.z.number().int().nonnegative().optional(),
    selectedStopId: zod_1.z.string().optional(),
    committedStops: zod_1.z.array(exports.voiceStopSchema).optional(),
    // Via points the voice-planned route went through, in route order — "load more" reuses them.
    routeWaypoints: zod_1.z.array(geo_1.coordTupleSchema).optional(),
    // Drive time used by a voice-planned route.
    remainingTimeSeconds: zod_1.z.number().positive().optional(),
    appliedFilters: exports.voiceRouteFiltersSchema.optional(),
    transcript: zod_1.z.string(),
    intent: zod_1.z.enum(exports.VOICE_USER_INTENTS),
    destination: zod_1.z.string().nullable(),
    awaitingUserReply: zod_1.z.boolean(),
});
// Frames streamed over POST /voice/agent as newline-delimited JSON. A discriminated union so added
// signals are a new variant, no transport change. A route turn streams an optional interim `ack`
// (spoken while the route is computed) followed by one `result` frame; every other turn streams a
// lone `result`. `.passthrough()` keeps `mapRoute` (heavy route geometry, deliberately off
// voiceAgentResponseSchema — see the backend's actionOutputSchema) on the wire.
exports.voiceAgentStreamFrameSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({ type: zod_1.z.literal('result'), data: exports.voiceAgentResponseSchema.passthrough() }),
    zod_1.z.object({ type: zod_1.z.literal('ack'), ack: zod_1.z.string() }),
]);
//# sourceMappingURL=voice.js.map